import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { Theme } from '@mui/material';

import CircleUserIcon from '@watershed/icons/components/CircleUser';
import CityIcon from '@watershed/icons/components/City';
import GroupsIcon from '@watershed/icons/components/Groups';
import HelpIcon from '@watershed/icons/components/Help';
import {
  routeForAccessSettings,
  routeForApiSettings,
  routeForFinanceSettings,
  routeForIntegrationsSettings,
  routeForLearningHub,
  routeForOrganizationPermissions,
  routeForOrganizationRoles,
  routeForOrganizationUsers,
  routeForPublicApiDocs,
  routeForSupport,
  routeForUserProfile,
  routeForWorkspaceSettings,
} from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';

export type SettingsNavigationItemType = {
  id: string;
  label: ReactNode;
  href: string;
  newTab?: boolean;
  testId?: string;
};

export type SettingsNavigationGroupType = {
  label: string;
  icon: ReactNode;
  items: Array<SettingsNavigationItemType>;
};

// Helper function to create icon with consistent styling
const createIcon = (Icon: typeof GroupsIcon) => (
  <Icon size={14} color={(theme: Theme) => theme.palette.grey50} />
);

function getWorkspaceGroup(
  canAccessFinance: boolean,
  workspaceLabel: string
): SettingsNavigationGroupType {
  return {
    label: workspaceLabel,
    icon: createIcon(CityIcon),
    items: [
      {
        id: 'preferences',
        label: (
          <Trans context="Settings Navigation workspace preferences">
            Preferences
          </Trans>
        ),
        href: routeForWorkspaceSettings(),
        testId: TestIds.PreferencesNavigationItem,
      },
      ...(canAccessFinance
        ? [
            {
              id: 'finance',
              label: (
                <Trans context="Settings Navigation finance">Finance</Trans>
              ),
              href: routeForFinanceSettings(),
            },
          ]
        : []),
      {
        id: 'access',
        label: (
          <Trans context="Settings Navigation access and security">
            Access and security
          </Trans>
        ),
        href: routeForAccessSettings(),
        testId: TestIds.AccessAndSecurityNavigationItem,
      },
      {
        id: 'integrations',
        label: (
          <Trans context="Settings Navigation integrations">Integrations</Trans>
        ),
        href: routeForIntegrationsSettings(),
        testId: TestIds.IntegrationsNavigationItem,
      },
      {
        id: 'api',
        label: <Trans context="Settings Navigation api">API</Trans>,
        href: routeForApiSettings(),
        testId: TestIds.ApiNavigationItem,
      },
    ],
  };
}

/**
 * Factory function to create settings navigation groups
 * This allows for better reusability and customization
 */
export function createSettingsNavigationGroups({
  canAccessFinance,
  isAdmin,
  isSupplierOrg,
  workspaceLabel = 'Workspace',
}: {
  canAccessFinance: boolean;
  isAdmin: boolean;
  isSupplierOrg: boolean;
  workspaceLabel?: string;
}): Array<SettingsNavigationGroupType> {
  const resolvedWorkspaceLabel = workspaceLabel.trim() || 'Workspace';
  return [
    {
      label: 'Personal',
      icon: createIcon(CircleUserIcon),
      items: [
        {
          id: 'profile',
          label: (
            <Trans context="Settings Navigation account settings">
              Account settings
            </Trans>
          ),
          href: routeForUserProfile(),
          testId: TestIds.AccountSettingsButton,
        },
      ],
    },
    ...(isAdmin
      ? [
          {
            label: 'User management',
            icon: createIcon(GroupsIcon),
            items: [
              {
                id: 'users',
                label: (
                  <Trans context="Settings Navigation user management users">
                    Users
                  </Trans>
                ),
                href: routeForOrganizationUsers(),
                testId: TestIds.UsersNavigationItem,
              },
              {
                id: 'roles',
                label: (
                  <Trans context="Settings Navigation user management roles">
                    Roles
                  </Trans>
                ),
                href: routeForOrganizationRoles(),
                testId: TestIds.RoleNavigationItem,
              },
              {
                id: 'permissions',
                label: (
                  <Trans context="Settings Navigation user management permissions">
                    Permissions
                  </Trans>
                ),
                href: routeForOrganizationPermissions(),
                testId: TestIds.PermissionsNavigationItem,
              },
            ],
          },
          ...(isSupplierOrg
            ? []
            : [getWorkspaceGroup(canAccessFinance, resolvedWorkspaceLabel)]),
        ]
      : []),
    ...(isSupplierOrg
      ? []
      : [
          {
            label: 'Help',
            icon: createIcon(HelpIcon),
            items: [
              {
                id: 'learning-hub',
                label: (
                  <Trans context="Settings Navigation learning hub">
                    Learning hub
                  </Trans>
                ),
                href: routeForLearningHub({ from: 'Sidebar' }),
              },
              {
                id: 'support',
                label: (
                  <Trans context="Settings Navigation support">Support</Trans>
                ),
                href: routeForSupport(),
              },
              {
                id: 'api-docs',
                label: (
                  <Trans context="Settings Navigation api docs">API docs</Trans>
                ),
                href: routeForPublicApiDocs(),
                newTab: true,
              },
            ],
          },
        ]),
  ];
}
